import { io, Socket } from "socket.io-client"
import { useAuthStore } from "@/stores/auth"
import { useRestaurantStore } from "@/stores/restaurant"
import { toast } from "vue-sonner"
import { apiService } from "@/services/api"
import { pushNotificationService } from "./pushNotification"
import { playNotificationSound } from "@/services/notificationSound"
import { i18n } from "@/i18n"

class WebSocketService {
  private _socket: Socket | null = null
  private isRefreshingToken = false // Flag to prevent multiple refresh attempts
  private refreshTokenListener: (() => void) | null = null // Listener for token refresh events
  private isConnecting = false // Flag to prevent parallel connection attempts
  private refreshAttempts = 0 // Counter for refresh attempts
  private maxRefreshAttempts = 3 // Maximum number of refresh attempts before giving up
  private lastRefreshAttempt = 0 // Timestamp of last refresh attempt
  private refreshCooldown = 2000 // Cooldown between refresh attempts (2 seconds)
  private scheduledRetry: ReturnType<typeof setTimeout> | null = null // Scheduled retry after cooldown
  private lastOrdersRefresh = 0 // Timestamp of last orders refresh (to prevent duplicate requests)
  private ordersRefreshCooldown = 2000 // Cooldown between orders refresh (2 seconds) - prevents duplicate requests from WebSocket + Push

  // Public getter for socket (read-only access)
  public get socket(): Socket | null {
    return this._socket
  }

  public disconnect(): void {
    if (this._socket) {
      // Remove all event listeners before disconnecting
      this._socket.removeAllListeners()
      this._socket.disconnect()
      this._socket = null
      // eslint-disable-next-line no-console
      console.log("🔌 [WebSocket] Disconnected from server")
    }

    // Reset flags
    this.isConnecting = false
    this.isRefreshingToken = false
    this.refreshAttempts = 0

    // Clear scheduled retry if exists
    if (this.scheduledRetry) {
      clearTimeout(this.scheduledRetry)
      this.scheduledRetry = null
      // eslint-disable-next-line no-console
      console.log("🧹 [WebSocket] Cleared scheduled retry on disconnect")
    }
    // eslint-disable-next-line no-console
    console.log("🔄 [WebSocket] Reset connection flags", {
      isConnecting: this.isConnecting,
      isRefreshingToken: this.isRefreshingToken,
      refreshAttempts: this.refreshAttempts,
    })

    // Remove token refresh listener
    if (this.refreshTokenListener && typeof globalThis !== "undefined" && "window" in globalThis) {
      const win = globalThis.window as any
      if (win && win.removeEventListener) {
        win.removeEventListener("token-refreshed", this.refreshTokenListener)
        // eslint-disable-next-line no-console
        console.log("🔌 [WebSocket] Removed token refresh listener")
      }
      this.refreshTokenListener = null
    }
  }

  public connect(): void {
    const authStore = useAuthStore()
    const restaurantStore = useRestaurantStore()

    // Tokens are now in HTTP-only cookies, check authentication status instead
    if (!authStore.isAuthenticated || !restaurantStore.restaurant?.id) {
      // eslint-disable-next-line no-console
      console.warn("⚠️ [WebSocket] Cannot connect - missing authentication or restaurant ID", {
        isAuthenticated: authStore.isAuthenticated,
        restaurantId: restaurantStore.restaurant?.id,
      })
      return
    }

    // Prevent parallel connection attempts
    if (this.isConnecting) {
      // eslint-disable-next-line no-console
      console.log("⏳ [WebSocket] Connection already in progress, skipping...")
      return
    }

    // If socket exists and is connected, don't reconnect
    if (this._socket && this._socket.connected) {
      // eslint-disable-next-line no-console
      console.log("✅ [WebSocket] Already connected, skipping reconnect")
      return
    }

    // If socket exists but is not connected, clean it up first
    if (this._socket && !this._socket.connected) {
      // eslint-disable-next-line no-console
      console.log("🧹 [WebSocket] Cleaning up old socket before reconnecting...")
      this._socket.removeAllListeners()
      this._socket.disconnect()
      this._socket = null
    }

    this.isConnecting = true
    // eslint-disable-next-line no-console
    console.log("🔌 [WebSocket] Attempting to connect...", {
      restaurantId: restaurantStore.restaurant?.id,
      isRefreshingToken: this.isRefreshingToken,
      refreshAttempts: this.refreshAttempts,
    })

    // Tokens are now in HTTP-only cookies, automatically sent by browser
    // Backend will extract token from cookies in handshake
    // Disable automatic reconnection - we'll handle it manually after token refresh
    // Note: extraHeaders may not work with WebSocket transport, so we also pass it in auth
    this._socket = io((import.meta.env.VITE_API_URL || "http://localhost:3000") + "/restaurant", {
      withCredentials: true, // Send cookies with WebSocket connection
      transports: ["websocket"],
      reconnection: false, // Disable automatic reconnection - we handle it manually
      reconnectionAttempts: 0,
      reconnectionDelay: 0,
      extraHeaders: {
        "X-Application": "backoffice", // Identify backoffice requests (may not work with WebSocket)
      },
      auth: {
        application: "backoffice", // Also pass in auth as fallback
      },
    })

    this._socket.on("connect", () => {
      // eslint-disable-next-line no-console
      console.log("✅ [WebSocket] Connected to server", {
        socketId: this._socket?.id,
        restaurantId: restaurantStore.restaurant!.id,
      })
      this.isConnecting = false

      // Clear scheduled retry if connection succeeded before retry fired
      if (this.scheduledRetry) {
        clearTimeout(this.scheduledRetry)
        this.scheduledRetry = null
        // eslint-disable-next-line no-console
        console.log("✅ [WebSocket] Cleared scheduled retry - connection succeeded")
      }

      // Don't reset refreshAttempts here - wait for successful restaurant join
      // Join the specific restaurant room
      // eslint-disable-next-line no-console
      console.log("🚪 [WebSocket] Joining restaurant room:", restaurantStore.restaurant!.id)

      // Validate socket connection state before emitting
      if (!this._socket) {
        // eslint-disable-next-line no-console
        console.error("❌ [WebSocket] Cannot join restaurant - socket is null")
        return
      }

      if (!this._socket.connected) {
        // eslint-disable-next-line no-console
        console.error("❌ [WebSocket] Cannot join restaurant - socket not connected", {
          socketId: this._socket.id,
          connected: this._socket.connected,
        })
        return
      }

      if (!this._socket.id) {
        // eslint-disable-next-line no-console
        console.error("❌ [WebSocket] Cannot join restaurant - socket has no ID", {
          connected: this._socket.connected,
        })
        return
      }

      // All validations passed, emit join_restaurant
      // eslint-disable-next-line no-console
      console.log("✅ [WebSocket] Socket validated, emitting join_restaurant", {
        socketId: this._socket.id,
        restaurantId: restaurantStore.restaurant!.id,
      })
      this._socket.emit("join_restaurant", {
        restaurantId: restaurantStore.restaurant!.id,
      })
    })

    this._socket.on("disconnect", reason => {
      console.warn(`WebSocket: Disconnected from server. Reason: ${reason}`)
      if (reason === "io server disconnect") {
        // The server has initiated the disconnect, so we need to reconnect manually
        this._socket?.connect()
      }
    })

    this._socket.on("connect_error", error => {
      // eslint-disable-next-line no-console
      console.error("❌ [WebSocket] Connection error:", {
        message: error.message,
        isConnecting: this.isConnecting,
      })
      this.isConnecting = false
      // Don't auto-reconnect on connection error - let error handler deal with it
      // This prevents infinite loops
    })

    // Handle websocket errors (e.g., access denied)
    this._socket.on("error", async data => {
      // eslint-disable-next-line no-console
      console.error("❌ [WebSocket] Server error:", {
        message: data.message,
        data,
        isRefreshingToken: this.isRefreshingToken,
        refreshAttempts: this.refreshAttempts,
      })

      // If "Not authenticated" error, try to refresh token and reconnect
      if (data.message === "Not authenticated") {
        // eslint-disable-next-line no-console
        console.log("🔐 [WebSocket] Handling 'Not authenticated' error")
        await this.handleNotAuthenticatedError()
        return
      }

      // Show error toast for other errors
      // eslint-disable-next-line no-console
      console.log("⚠️ [WebSocket] Showing error toast for non-auth error")
      toast.error("Błąd websocket", {
        description: data.message || "Nieoczekiwany błąd połączenia",
      })
    })

    // Initialize token refresh listener (only once)
    this.initTokenRefreshListener()

    // Handle successful restaurant join
    this._socket.on("restaurant_joined", data => {
      // eslint-disable-next-line no-console
      console.log("✅ [WebSocket] Successfully joined restaurant", {
        restaurantId: data.restaurantId,
        socketId: this._socket?.id,
        previousRefreshAttempts: this.refreshAttempts,
      })
      // Reset refresh attempts only after successful restaurant join
      this.refreshAttempts = 0
      // eslint-disable-next-line no-console
      console.log("🔄 [WebSocket] Reset refreshAttempts after successful restaurant join", {
        refreshAttempts: this.refreshAttempts,
      })
    })

    this._socket.on("new_order", async (data: any) => {
      const isReorder = data.type === "reorder" || data.data?.isReorder === true;
      const title = isReorder ? "Domówienie do zamówienia!" : "Nowe zamówienie!";
      
      // eslint-disable-next-line no-console
      console.log(`🔔 [WebSocket] ${isReorder ? "Reorder" : "New order"} received:`, {
        orderId: data.data.order?.id,
        orderNumber: data.data.order?.orderNumber,
        isReorder,
        timestamp: new Date().toISOString(),
        isAppHidden: document.hidden,
      })

      // Play notification sound (for both new orders and reordering)
      playNotificationSound()

      // Refresh orders with retry mechanism
      await this.refreshOrdersWithRetry(
        restaurantStore.restaurant!.id,
        data.data.order?.id,
        data.data.order?.orderNumber
      )

      // Show notification based on app visibility
      if (document.hidden) {
        // App is in background - show push notification
        try {
          await pushNotificationService.showNotification(title, {
            body: isReorder
              ? `Domówienie do zamówienia #${data.data.order.orderNumber}`
              : `Zamówienie #${data.data.order.orderNumber} od ${
                  data.data.order.customerEmail || "Anonimowy"
                }`,
            tag: `order-${data.data.order?.id}`,
            data: {
              type: isReorder ? "reorder" : "new_order",
              orderId: data.data.order?.id,
              orderNumber: data.data.order?.orderNumber,
              isReorder,
            },
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn("⚠️ [WebSocket] Could not show push notification:", error)
        }
      } else {
        // App is in foreground - show toast
        toast(title, {
          description: isReorder
            ? `Domówienie do zamówienia #${data.data.order.orderNumber}`
            : `Zamówienie #${data.data.order.orderNumber} od ${
                data.data.order.customerEmail || "Anonimowy"
              }`,
          duration: 10000,
        })
      }
    })

    // Handle payment_completed event (dedicated event for payment completion)
    this._socket.on("payment_completed", async (data: any) => {
      // eslint-disable-next-line no-console
      console.log("💳 [WebSocket] Payment completed event received:", {
        orderId: data.data.order?.id,
        orderNumber: data.data.order?.orderNumber,
        paymentId: data.data.paymentId,
        timestamp: new Date().toISOString(),
        isAppHidden: document.hidden,
      })

      // Play notification sound
      playNotificationSound()

      // Refresh orders with retry mechanism
      await this.refreshOrdersWithRetry(
        restaurantStore.restaurant!.id,
        data.data.order?.id,
        data.data.order?.orderNumber
      )

      // Show notification based on app visibility
      if (document.hidden) {
        // App is in background - show push notification
        try {
          await pushNotificationService.showNotification("Płatność zakończona!", {
            body: `Zamówienie #${data.data.order?.orderNumber || "N/A"} zostało opłacone`,
            tag: `payment-${data.data.paymentId}`,
            data: {
              type: "payment_completed",
              orderId: data.data.order?.id,
              orderNumber: data.data.order?.orderNumber,
              paymentId: data.data.paymentId,
            },
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn("⚠️ [WebSocket] Could not show push notification:", error)
        }
      } else {
        // App is in foreground - show toast
        toast.success("Płatność zakończona!", {
          description: `Zamówienie #${data.data.order?.orderNumber || "N/A"} zostało opłacone`,
          duration: 5000,
        })
      }
    })

    this._socket.on("order_status_changed", async (data: any) => {
      console.log("WebSocket: Order status changed:", data)

      const rawStatus: string | undefined = data?.data?.status
      const orderId: string | undefined = data?.data?.orderId

      // Translate status using i18n; fall back to raw status if missing
      const localizedStatus =
        (rawStatus
          ? i18n.global.t(`orderDetails.statusLabels.${rawStatus}`)
          : "") || rawStatus || ""

      // Don't play sound for status changes - only for new orders and reordering

      // Refresh orders with deduplication
      const now = Date.now()
      const timeSinceLastRefresh = now - this.lastOrdersRefresh
      
      if (timeSinceLastRefresh >= this.ordersRefreshCooldown) {
        this.lastOrdersRefresh = now
        restaurantStore.fetchOrders(restaurantStore.restaurant!.id)
        restaurantStore.fetchActiveOrders(restaurantStore.restaurant!.id)
      } else {
        // eslint-disable-next-line no-console
        console.log(
          `⏭️ [WebSocket] Skipping orders refresh for status change - too soon after last refresh (${timeSinceLastRefresh}ms ago)`
        )
      }

      // Show notification based on app visibility
      if (orderId) {
        const shortOrderId = orderId.slice(-8)

        if (document.hidden) {
          // App is in background - show push notification
          try {
            await pushNotificationService.showNotification("Status zamówienia zmieniony", {
              body: `Zamówienie #${shortOrderId} ma nowy status: ${localizedStatus}`,
              tag: `order-status-${orderId}`,
              data: {
                type: "order_status_changed",
                orderId,
                status: rawStatus,
              },
            })
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn("⚠️ [WebSocket] Could not show push notification:", error)
          }
        } else {
          // App is in foreground - show toast
          toast.info("Status zamówienia zmieniony", {
            description: `Zamówienie #${shortOrderId} ma nowy status: ${localizedStatus}`,
          })
        }
      }
    })

    this._socket.on("cart_changed", (data: any) => {
      console.log("WebSocket: Cart changed:", data)

      // Optionally refresh related data here if needed in the future

      toast?.info("Koszyk zaktualizowany", {
        description: `Koszyk #${data.data.cart.id.slice(-8)} został zaktualizowany.`,
      })
    })

    this._socket.on("waiter_called", async (data: any) => {
      console.log("WebSocket: Waiter called:", data)

      // Play notification sound for waiter call (important event)
      playNotificationSound()

      // Refresh active orders to show waiter called banner
      restaurantStore.fetchActiveOrders(restaurantStore.restaurant!.id)

      // Show notification based on app visibility
      if (document.hidden) {
        // App is in background - show push notification
        try {
          await pushNotificationService.showNotification("Wezwano obsługę kelnerską!", {
            body: `Zamówienie #${
              data.data.order?.orderNumber || data.data.orderId?.slice(-8)
            } - klient wezwał obsługę kelnerską`,
            tag: `waiter-called-${data.data.orderId || data.data.order?.id}`,
            requireInteraction: true, // Important notification
            data: {
              type: "waiter_called",
              orderId: data.data.orderId || data.data.order?.id,
              orderNumber: data.data.order?.orderNumber,
            },
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn("⚠️ [WebSocket] Could not show push notification:", error)
        }
      } else {
        // App is in foreground - show toast
        toast("Wezwano obsługę kelnerską!", {
          description: `Zamówienie #${
            data.data.order?.orderNumber || data.data.orderId?.slice(-8)
          } - klient wezwał obsługę kelnerską`,
          duration: 10000,
        })
      }
    })
  }

  // Handle "Not authenticated" error
  private async handleNotAuthenticatedError(): Promise<void> {
    // eslint-disable-next-line no-console
    console.log("🔐 [WebSocket] handleNotAuthenticatedError called", {
      isRefreshingToken: this.isRefreshingToken,
      refreshAttempts: this.refreshAttempts,
      maxRefreshAttempts: this.maxRefreshAttempts,
    })

    // Check cooldown
    const now = Date.now()
    const timeSinceLastAttempt = now - this.lastRefreshAttempt
    if (timeSinceLastAttempt < this.refreshCooldown) {
      const remainingCooldown = this.refreshCooldown - timeSinceLastAttempt
      // eslint-disable-next-line no-console
      console.log("⏳ [WebSocket] Refresh cooldown active, scheduling retry...", {
        timeSinceLastAttempt,
        cooldown: this.refreshCooldown,
        remainingCooldown,
        lastRefreshAttempt: new Date(this.lastRefreshAttempt).toISOString(),
        currentTime: new Date(now).toISOString(),
        refreshAttempts: this.refreshAttempts,
      })

      // Schedule retry after cooldown expires
      if (!this.scheduledRetry) {
        // eslint-disable-next-line no-console
        console.log(`⏰ [WebSocket] Scheduling retry after ${remainingCooldown}ms`, {
          scheduledAt: new Date(now + remainingCooldown).toISOString(),
        })
        this.scheduledRetry = setTimeout(() => {
          this.scheduledRetry = null
          // eslint-disable-next-line no-console
          console.log("🔄 [WebSocket] Cooldown expired, retrying handleNotAuthenticatedError", {
            timestamp: new Date().toISOString(),
            refreshAttempts: this.refreshAttempts,
          })
          this.handleNotAuthenticatedError()
        }, remainingCooldown)
      } else {
        // eslint-disable-next-line no-console
        console.log("⏳ [WebSocket] Retry already scheduled, skipping...", {
          existingRetry: !!this.scheduledRetry,
        })
      }
      return
    }

    // Check if we've exceeded max refresh attempts
    if (this.refreshAttempts >= this.maxRefreshAttempts) {
      // eslint-disable-next-line no-console
      console.error("🛑 [WebSocket] Max refresh attempts reached. Stopping refresh attempts.", {
        refreshAttempts: this.refreshAttempts,
        maxRefreshAttempts: this.maxRefreshAttempts,
      })
      toast.error("Błąd uwierzytelniania", {
        description: "Nie udało się odświeżyć tokenu. Odśwież stronę i zaloguj się ponownie.",
      })
      return
    }

    // Prevent multiple refresh attempts
    if (this.isRefreshingToken) {
      // eslint-disable-next-line no-console
      console.log("⏳ [WebSocket] Token refresh already in progress, skipping...")
      return
    }

    // Check if ApiService is already refreshing
    const apiIsRefreshing = apiService.getIsRefreshing()
    // eslint-disable-next-line no-console
    console.log("🔍 [WebSocket] Checking ApiService refresh status:", {
      apiIsRefreshing,
    })

    if (apiIsRefreshing) {
      // eslint-disable-next-line no-console
      console.log("⏳ [WebSocket] ApiService is already refreshing, waiting...")
      try {
        await apiService.waitForTokenRefresh()
        // eslint-disable-next-line no-console
        console.log("✅ [WebSocket] ApiService refresh completed, reconnecting...")
        await this.reconnectAfterRefresh()
        return
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("❌ [WebSocket] ApiService refresh failed:", error)
        this.refreshAttempts++
        this.lastRefreshAttempt = Date.now()
        // eslint-disable-next-line no-console
        console.log("📊 [WebSocket] Updated refresh attempts after ApiService failure", {
          refreshAttempts: this.refreshAttempts,
        })
        return
      }
    }

    this.isRefreshingToken = true
    this.refreshAttempts++
    this.lastRefreshAttempt = Date.now()
    // eslint-disable-next-line no-console
    console.log(
      `🔄 [WebSocket] Token expired, attempting to refresh and reconnect... (attempt ${this.refreshAttempts}/${this.maxRefreshAttempts})`,
      {
        isRefreshingToken: this.isRefreshingToken,
        refreshAttempts: this.refreshAttempts,
        lastRefreshAttempt: new Date(this.lastRefreshAttempt).toISOString(),
      }
    )

    try {
      // Use ApiService to refresh token - it will handle the refresh through interceptor
      // We trigger refresh by making a call that will fail with 401, which triggers refresh
      // Or we can call refresh endpoint directly through apiService
      // eslint-disable-next-line no-console
      console.log("🔄 [WebSocket] Calling apiService.postRaw('/auth/refresh')...")
      await apiService.postRaw(
        "/auth/refresh",
        {},
        {
          headers: {
            "X-Application": "backoffice",
          },
        }
      )

      // eslint-disable-next-line no-console
      console.log("✅ [WebSocket] Token refresh API call successful, checking ApiService status...")

      // Wait for ApiService to finish if it's still refreshing
      const stillRefreshing = apiService.getIsRefreshing()
      if (stillRefreshing) {
        // eslint-disable-next-line no-console
        console.log("⏳ [WebSocket] ApiService still refreshing, waiting...")
        await apiService.waitForTokenRefresh()
        // eslint-disable-next-line no-console
        console.log("✅ [WebSocket] ApiService refresh completed")
      } else {
        // eslint-disable-next-line no-console
        console.log("✅ [WebSocket] ApiService not refreshing, proceeding with reconnect")
      }

      // Reconnect after successful refresh
      // eslint-disable-next-line no-console
      console.log("🔄 [WebSocket] Starting reconnect after successful refresh")
      await this.reconnectAfterRefresh()
    } catch (refreshError) {
      // eslint-disable-next-line no-console
      console.error("❌ [WebSocket] Failed to refresh token:", {
        error: refreshError,
        refreshAttempts: this.refreshAttempts,
        maxRefreshAttempts: this.maxRefreshAttempts,
      })
      this.isRefreshingToken = false

      // If refresh failed, show error
      if (this.refreshAttempts >= this.maxRefreshAttempts) {
        // eslint-disable-next-line no-console
        console.error("🛑 [WebSocket] Max refresh attempts reached, showing error toast")
        toast.error("Błąd uwierzytelniania", {
          description: "Nie udało się odświeżyć tokenu. Odśwież stronę i zaloguj się ponownie.",
        })
      }
    }
  }

  // Reconnect after successful token refresh
  private async reconnectAfterRefresh(): Promise<void> {
    // eslint-disable-next-line no-console
    console.log("🔄 [WebSocket] reconnectAfterRefresh called", {
      isRefreshingToken: this.isRefreshingToken,
      socketExists: !!this._socket,
      socketConnected: this._socket?.connected,
    })

    this.isRefreshingToken = false

    // Force complete socket disconnection - explicit cleanup sequence
    if (this._socket) {
      // eslint-disable-next-line no-console
      console.log("🧹 [WebSocket] Force disconnecting socket before reconnect", {
        socketId: this._socket.id,
        connected: this._socket.connected,
        listenersCount:
          this._socket.listeners("connect").length + this._socket.listeners("error").length,
      })

      // Step 1: Remove all listeners first
      this._socket.removeAllListeners()
      // eslint-disable-next-line no-console
      console.log("✅ [WebSocket] All listeners removed")

      // Step 2: Disconnect socket
      this._socket.disconnect()
      // eslint-disable-next-line no-console
      console.log("✅ [WebSocket] Socket disconnected")

      // Step 3: Set to null
      this._socket = null
      // eslint-disable-next-line no-console
      console.log("✅ [WebSocket] Socket set to null")
    }

    // Verify socket is null before proceeding
    if (this._socket !== null) {
      // eslint-disable-next-line no-console
      console.error("❌ [WebSocket] Socket not properly cleaned up, forcing null")
      this._socket = null
    }

    // Wait for cookies to be updated (increased delay to ensure browser has updated cookies)
    // eslint-disable-next-line no-console
    console.log("⏳ [WebSocket] Waiting 2000ms for cookies to be updated...", {
      socketIsNull: this._socket === null,
      timestamp: new Date().toISOString(),
      refreshAttempts: this.refreshAttempts,
    })
    await new Promise(resolve => setTimeout(resolve, 2000))
    // eslint-disable-next-line no-console
    console.log("✅ [WebSocket] Cookie update delay completed, reconnecting...", {
      socketIsNull: this._socket === null,
      timestamp: new Date().toISOString(),
      cookiesShouldBeUpdated: true,
    })

    // Verify socket is still null before reconnecting
    if (this._socket !== null) {
      // eslint-disable-next-line no-console
      console.error("❌ [WebSocket] Socket is not null before reconnect, forcing cleanup")
      // Store reference before nulling to avoid TypeScript narrowing issues
      const socketToCleanup: Socket = this._socket
      socketToCleanup.removeAllListeners()
      socketToCleanup.disconnect()
      this._socket = null
    }

    // Reconnect
    this.connect()
  }

  // Refresh orders with retry mechanism and deduplication
  private async refreshOrdersWithRetry(
    restaurantId: string,
    orderId: string | undefined,
    orderNumber: string | undefined,
    maxRetries: number = 3,
    retryDelay: number = 500
  ): Promise<void> {
    const restaurantStore = useRestaurantStore()

    // Deduplication: Check if orders were refreshed recently (prevents duplicate requests from WebSocket + Push)
    const now = Date.now()
    const timeSinceLastRefresh = now - this.lastOrdersRefresh
    
    if (timeSinceLastRefresh < this.ordersRefreshCooldown) {
      // eslint-disable-next-line no-console
      console.log(`⏭️ [WebSocket] Skipping orders refresh - too soon after last refresh (${timeSinceLastRefresh}ms ago)`, {
        orderId,
        orderNumber,
        timeSinceLastRefresh,
        cooldown: this.ordersRefreshCooldown,
      })
      return
    }

    // Update last refresh timestamp
    this.lastOrdersRefresh = now

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // eslint-disable-next-line no-console
        console.log(`🔄 [WebSocket] Refreshing orders (attempt ${attempt + 1}/${maxRetries})`, {
          orderId,
          orderNumber,
        })

        // Refresh both orders lists
        await Promise.all([
          restaurantStore.fetchOrders(restaurantId),
          restaurantStore.fetchActiveOrders(restaurantId),
        ])

        // Wait a bit for store to update
        await new Promise(resolve => setTimeout(resolve, retryDelay))

        // Check if order appeared in activeOrders (if orderId is provided)
        if (orderId) {
          const orderFound = restaurantStore.activeOrders.some(order => order.id === orderId)

          if (orderFound) {
            // eslint-disable-next-line no-console
            console.log("✅ [WebSocket] Order found in activeOrders after refresh", {
              orderId,
              orderNumber,
              attempt: attempt + 1,
            })
            return // Success - order is on the list
          } else if (attempt < maxRetries - 1) {
            // eslint-disable-next-line no-console
            console.warn(
              `⚠️ [WebSocket] Order not found in activeOrders, retrying... (attempt ${
                attempt + 1
              }/${maxRetries})`,
              {
                orderId,
                orderNumber,
                activeOrdersCount: restaurantStore.activeOrders.length,
              }
            )
            // Wait before retry
            await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)))
          } else {
            // eslint-disable-next-line no-console
            console.error("❌ [WebSocket] Order not found in activeOrders after all retries", {
              orderId,
              orderNumber,
              attempts: maxRetries,
              activeOrdersCount: restaurantStore.activeOrders.length,
            })
          }
        } else {
          // If no orderId provided, just refresh and return
          return
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`❌ [WebSocket] Error refreshing orders (attempt ${attempt + 1}):`, error)
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)))
        }
      }
    }
  }

  // Initialize token refresh listener (called once)
  public initTokenRefreshListener(): void {
    // Only add listener if it doesn't exist yet
    if (this.refreshTokenListener) {
      return
    }

    if (typeof globalThis !== "undefined" && "window" in globalThis) {
      const win = globalThis.window as any
      if (win && win.addEventListener) {
        this.refreshTokenListener = () => {
          // eslint-disable-next-line no-console
          console.log(
            "🔄 [WebSocket] Token refreshed proactively (from ApiService), reconnecting...",
            {
              socketExists: !!this._socket,
              socketConnected: this._socket?.connected,
            }
          )
          // Disconnect and reconnect to use new cookies
          if (this._socket && this._socket.connected) {
            // eslint-disable-next-line no-console
            console.log("🧹 [WebSocket] Disconnecting socket for proactive refresh reconnect")
            this._socket.removeAllListeners()
            this._socket.disconnect()
            this._socket = null

            // Wait a bit for cookies to be updated, then reconnect
            // eslint-disable-next-line no-console
            console.log("⏳ [WebSocket] Waiting 2000ms before reconnect (proactive refresh)...")
            setTimeout(() => {
              // eslint-disable-next-line no-console
              console.log("🔄 [WebSocket] Reconnecting after proactive refresh...", {
                socketIsNull: this._socket === null,
              })
              this.connect()
            }, 2000) // Increased delay to ensure cookies are updated
          } else {
            // eslint-disable-next-line no-console
            console.log("⚠️ [WebSocket] Socket not connected, skipping proactive refresh reconnect")
          }
        }
        win.addEventListener("token-refreshed", this.refreshTokenListener)
        // eslint-disable-next-line no-console
        console.log("👂 [WebSocket] Token refresh listener initialized")
      }
    }
  }
}

export const webSocketService = new WebSocketService()
// Initialize token refresh listener once when module loads
webSocketService.initTokenRefreshListener()
